import { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import PeopleIcon from '@mui/icons-material/People';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ApiResponse } from '@tinyrewards/shared';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface AdminUser {
    userID: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName: string;
    isAdmin: boolean;
    isActive: boolean;
    dateCreated: string;
    lastLogin: string | null;
    totalBadgesCreated: number;
    totalBadgesEarned: number;
    totalIssuersCreated: number;
}

interface AdminIssuer {
    issuerID: string;
    issuerName: string;
    issuerType: string;
    organizationType: string | null;
    creatorName: string | null;
    imageUrl: string | null;
    totalBadgesCreated: number;
    totalActiveBadges: number;
    totalInactiveBadges: number;
    isActive: boolean;
    dateCreated: string;
}

interface AdminBadge {
    openBadgeID: string;
    badgeName: string;
    issuerName: string;
    image: string | null;
    criteriaHTML: string | null;
    isActive: boolean;
    isPrivate: boolean;
    createdDate: string;
    totalBadgesIssued: number;
    totalBadgesRevoked: number;
    firstIssuedDate: string | null;
    lastIssuedDate: string | null;
}

interface AdminStats {
    userCount: number;
    badgeCount: number;
    issuedBadgeCount: number;
}

interface PaginatedResponse<T> {
    success: boolean;
    data?: T[];
    message?: string;
    total?: number;
    page?: number;
    pageSize?: number;
}

const PAGE_SIZE = 20;

/** Convert PascalCase/camelCase to "Title Case With Spaces" */
function splitCamelCase(s: string): string {
    return s.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** Strip HTML tags from criteria markup and collapse whitespace for table display */
function stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const CRITERIA_MAX_LEN = 120;

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminPage() {
    const [activeTab, setActiveTab] = useState<'users' | 'issuers' | 'badges'>('users');
    const { refresh } = useAuth();
    const navigate = useNavigate();

    // Stats
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Users tab
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersPage, setUsersPage] = useState(1);
    const [usersSearch, setUsersSearch] = useState('');
    const [usersSearchInput, setUsersSearchInput] = useState('');
    const [usersStatus, setUsersStatus] = useState<'active' | 'inactive' | 'all'>('active');
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState<string | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // Issuers tab
    const [issuers, setIssuers] = useState<AdminIssuer[]>([]);
    const [issuersTotal, setIssuersTotal] = useState(0);
    const [issuersPage, setIssuersPage] = useState(1);
    const [issuersSearch, setIssuersSearch] = useState('');
    const [issuersSearchInput, setIssuersSearchInput] = useState('');
    const [issuersLoading, setIssuersLoading] = useState(false);
    const [issuersError, setIssuersError] = useState<string | null>(null);
    const [issuersLoaded, setIssuersLoaded] = useState(false);

    // Badges tab
    const [badges, setBadges] = useState<AdminBadge[]>([]);
    const [badgesTotal, setBadgesTotal] = useState(0);
    const [badgesPage, setBadgesPage] = useState(1);
    const [badgesSearch, setBadgesSearch] = useState('');
    const [badgesSearchInput, setBadgesSearchInput] = useState('');
    const [badgesLoading, setBadgesLoading] = useState(false);
    const [badgesError, setBadgesError] = useState<string | null>(null);
    const [badgesLoaded, setBadgesLoaded] = useState(false);
    const [badgeActionId, setBadgeActionId] = useState<string | null>(null);
    const [badgesType, setBadgesType] = useState<'all' | 'regular' | 'learningpath' | 'manual'>('all');
    const [badgesPublicity, setBadgesPublicity] = useState<'all' | 'public' | 'private'>('all');

    // ─── Stats ──────────────────────────────────────────────────────────────

    useEffect(() => {
        setStatsLoading(true);
        apiGet<AdminStats>('/api/admin/stats')
            .then(res => { if (res.success && res.data) setStats(res.data); })
            .finally(() => setStatsLoading(false));
    }, []);

    // ─── Users ──────────────────────────────────────────────────────────────

    const loadUsers = useCallback(() => {
        setUsersLoading(true);
        setUsersError(null);
        apiGet<never>(`/api/admin/users?page=${usersPage}&pageSize=${PAGE_SIZE}&search=${encodeURIComponent(usersSearch)}&status=${usersStatus}`)
            .then(res => {
                const typed = res as unknown as PaginatedResponse<AdminUser>;
                if (typed.success && typed.data) {
                    setUsers(typed.data);
                    setUsersTotal(typed.total ?? typed.data.length);
                } else {
                    setUsersError(typed.message ?? 'Failed to load users.');
                }
            })
            .catch(() => setUsersError('An unexpected error occurred.'))
            .finally(() => setUsersLoading(false));
    }, [usersPage, usersSearch, usersStatus]);

    useEffect(() => { loadUsers(); }, [loadUsers]);

    const handleUsersSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setUsersPage(1);
        setUsersSearch(usersSearchInput);
    };

    const handleToggleActive = async (adminUser: AdminUser) => {
        setTogglingId(adminUser.userID);
        try {
            const res = await apiPut<{ userID: string; isActive: boolean }>(`/api/admin/users/${adminUser.userID}/toggle-active`, {});
            if (res.success && res.data) {
                setUsers(prev => prev.map(u => u.userID === adminUser.userID ? { ...u, isActive: res.data!.isActive } : u));
            } else {
                alert(res.message ?? 'Failed to toggle user status.');
            }
        } catch {
            alert('An unexpected error occurred.');
        } finally {
            setTogglingId(null);
        }
    };

    const handleDelete = async (userId: string) => {
        if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
        setDeletingId(userId);
        try {
            const res: ApiResponse<void> = await apiDelete<void>(`/api/admin/users/${userId}`);
            if (res.success) {
                setUsers(prev => prev.filter(u => u.userID !== userId));
                setUsersTotal(prev => prev - 1);
            } else {
                alert(res.message ?? 'Failed to delete user.');
            }
        } catch {
            alert('An unexpected error occurred.');
        } finally {
            setDeletingId(null);
        }
    };

    const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
    const handleImpersonate = async (adminUser: AdminUser) => {
        if (!confirm(`Impersonate ${adminUser.displayName} (${adminUser.email})? You will see the app as they do until you return to your admin account.`)) return;
        setImpersonatingId(adminUser.userID);
        try {
            const res = await apiPost<void>(`/api/admin/users/${adminUser.userID}/impersonate`, {});
            if (res.success) {
                await refresh();
                navigate('/available-badges');
            } else {
                alert(res.message ?? 'Failed to impersonate user.');
            }
        } catch {
            alert('An unexpected error occurred.');
        } finally {
            setImpersonatingId(null);
        }
    };

    // ─── Issuers ────────────────────────────────────────────────────────────

    const loadIssuers = useCallback(() => {
        setIssuersLoading(true);
        setIssuersError(null);
        apiGet<never>(`/api/admin/issuers?page=${issuersPage}&pageSize=${PAGE_SIZE}&search=${encodeURIComponent(issuersSearch)}`)
            .then(res => {
                const typed = res as unknown as PaginatedResponse<AdminIssuer>;
                if (typed.success && typed.data) {
                    setIssuers(typed.data);
                    setIssuersTotal(typed.total ?? typed.data.length);
                    setIssuersLoaded(true);
                } else {
                    setIssuersError(typed.message ?? 'Failed to load issuers.');
                }
            })
            .catch(() => setIssuersError('An unexpected error occurred.'))
            .finally(() => setIssuersLoading(false));
    }, [issuersPage, issuersSearch]);

    // Lazy-load issuers when the tab is first opened, and reload on page/search change
    useEffect(() => {
        if (activeTab === 'issuers') loadIssuers();
    }, [activeTab, loadIssuers]);

    const handleIssuersSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setIssuersPage(1);
        setIssuersSearch(issuersSearchInput);
    };

    // ─── Badges ─────────────────────────────────────────────────────────────

    const loadBadges = useCallback(() => {
        setBadgesLoading(true);
        setBadgesError(null);
        apiGet<never>(`/api/admin/badges?page=${badgesPage}&pageSize=${PAGE_SIZE}&search=${encodeURIComponent(badgesSearch)}&type=${badgesType}&publicity=${badgesPublicity}`)
            .then(res => {
                const typed = res as unknown as PaginatedResponse<AdminBadge>;
                if (typed.success && typed.data) {
                    setBadges(typed.data);
                    setBadgesTotal(typed.total ?? typed.data.length);
                    setBadgesLoaded(true);
                } else {
                    setBadgesError(typed.message ?? 'Failed to load badges.');
                }
            })
            .catch(() => setBadgesError('An unexpected error occurred.'))
            .finally(() => setBadgesLoading(false));
    }, [badgesPage, badgesSearch, badgesType, badgesPublicity]);

    useEffect(() => {
        if (activeTab === 'badges') loadBadges();
    }, [activeTab, loadBadges]);

    const handleBadgesSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setBadgesPage(1);
        setBadgesSearch(badgesSearchInput);
    };

    const handleBadgeDelete = async (badge: AdminBadge) => {
        if (!confirm(`Delete "${badge.badgeName}"? This cannot be undone.`)) return;
        setBadgeActionId(badge.openBadgeID);
        try {
            const res = await apiDelete<void>(`/api/admin/badges/${badge.openBadgeID}`);
            if (res.success) {
                setBadges(prev => prev.filter(b => b.openBadgeID !== badge.openBadgeID));
                setBadgesTotal(prev => prev - 1);
            } else {
                alert(res.message ?? 'Failed to delete badge.');
            }
        } catch {
            alert('An unexpected error occurred.');
        } finally {
            setBadgeActionId(null);
        }
    };

    const handleBadgeReset = async (badge: AdminBadge) => {
        if (!confirm(`Reset "${badge.badgeName}"? This will delete all task completion records for this badge.`)) return;
        setBadgeActionId(badge.openBadgeID);
        try {
            const res = await apiPost<void>(`/api/admin/badges/${badge.openBadgeID}/reset`, {});
            if (res.success) {
                setBadges(prev => prev.map(b =>
                    b.openBadgeID === badge.openBadgeID
                        ? { ...b, totalBadgesIssued: 0, totalBadgesRevoked: 0, firstIssuedDate: null, lastIssuedDate: null }
                        : b,
                ));
            } else {
                alert(res.message ?? 'Failed to reset badge.');
            }
        } catch {
            alert('An unexpected error occurred.');
        } finally {
            setBadgeActionId(null);
        }
    };

    const handleBadgeTransfer = async (badge: AdminBadge) => {
        if (!confirm(`Transfer "${badge.badgeName}" to the default TinyAward issuer?`)) return;
        setBadgeActionId(badge.openBadgeID);
        try {
            const res = await apiPost<void>(`/api/admin/badges/${badge.openBadgeID}/transfer`, {});
            if (res.success) {
                loadBadges();
            } else {
                alert(res.message ?? 'Failed to transfer badge.');
            }
        } catch {
            alert('An unexpected error occurred.');
        } finally {
            setBadgeActionId(null);
        }
    };

    // ─── Derived ────────────────────────────────────────────────────────────

    const usersTotalPages = Math.ceil(usersTotal / PAGE_SIZE);
    const issuersTotalPages = Math.ceil(issuersTotal / PAGE_SIZE);
    const badgesTotalPages = Math.ceil(badgesTotal / PAGE_SIZE);

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight={700} mb={3}>Admin Dashboard</Typography>

            {/* Stats Cards */}
            <Grid container spacing={3} mb={4}>
                {statsLoading ? (
                    <Grid item xs={12}><Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box></Grid>
                ) : stats ? (
                    <>
                        {[
                            { icon: <PeopleIcon sx={{ fontSize: 40, color: 'accent.main' }} />, value: stats.userCount, label: 'Total Users' },
                            { icon: <WorkspacePremiumIcon sx={{ fontSize: 40, color: 'accent.main' }} />, value: stats.badgeCount, label: 'Total Badges' },
                            { icon: <SendIcon sx={{ fontSize: 40, color: 'accent.main' }} />, value: stats.issuedBadgeCount, label: 'Badges Issued' },
                        ].map(s => (
                            <Grid item xs={12} md={4} key={s.label}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                        {s.icon}
                                        <Typography variant="h4" fontWeight={700} mt={1}>{s.value.toLocaleString()}</Typography>
                                        <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </>
                ) : null}
            </Grid>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Users" value="users" icon={<PeopleIcon fontSize="small" />} iconPosition="start" />
                <Tab label="Issuers" value="issuers" icon={<BusinessIcon fontSize="small" />} iconPosition="start" />
                <Tab label="Badges" value="badges" icon={<WorkspacePremiumIcon fontSize="small" />} iconPosition="start" />
            </Tabs>

            {/* Users Panel */}
            {activeTab === 'users' && (
                <Paper elevation={0} sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.3)', borderRadius: 3 }}>
                    <Box sx={{ px: 2, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={600}>
                            Users
                            {usersTotal > 0 && <Chip label={usersTotal} size="small" sx={{ ml: 1 }} />}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={usersStatus}
                                onChange={(_, value: 'active' | 'inactive' | 'all' | null) => {
                                    if (value === null) return;
                                    setUsersStatus(value);
                                    setUsersPage(1);
                                }}
                            >
                                <ToggleButton value="active">Active</ToggleButton>
                                <ToggleButton value="inactive">Inactive</ToggleButton>
                                <ToggleButton value="all">All</ToggleButton>
                            </ToggleButtonGroup>
                            <Box component="form" display="flex" gap={1} onSubmit={handleUsersSearch}>
                                <TextField
                                    size="small"
                                    placeholder="Search users..."
                                    value={usersSearchInput}
                                    onChange={e => setUsersSearchInput(e.target.value)}
                                    sx={{ width: 220 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                    }}
                                />
                                <Button type="submit">Search</Button>
                            </Box>
                        </Box>
                    </Box>

                    {usersLoading && (
                        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                    )}
                    {!usersLoading && usersError && (
                        <Box p={2}><Alert severity="error">{usersError}</Alert></Box>
                    )}
                    {!usersLoading && !usersError && users.length === 0 && (
                        <Box textAlign="center" py={6} color="text.secondary">
                            <PersonOffIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Typography mt={1}>No users found.</Typography>
                        </Box>
                    )}
                    {!usersLoading && !usersError && users.length > 0 && (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#2A1870', '& .MuiTableCell-head': { color: '#fff' } }}>
                                        <TableRow>
                                            <TableCell><strong>Name</strong></TableCell>
                                            <TableCell><strong>Email</strong></TableCell>
                                            <TableCell><strong>Last Login</strong></TableCell>
                                            <TableCell align="center"><strong>Badges Created</strong></TableCell>
                                            <TableCell align="center"><strong>Badges Earned</strong></TableCell>
                                            <TableCell align="center"><strong>Issuers Created</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Admin</strong></TableCell>
                                            <TableCell><strong>Joined</strong></TableCell>
                                            <TableCell align="right"><strong>Actions</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map(u => (
                                            <TableRow key={u.userID} hover>
                                                <TableCell><strong>{u.displayName}</strong></TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight={600}>{u.totalBadgesCreated}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight={600}>{u.totalBadgesEarned}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight={600}>{u.totalIssuersCreated}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={u.isActive ? 'Active' : 'Inactive'} color={u.isActive ? 'success' : 'default'} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    {u.isAdmin && <CheckCircleIcon fontSize="small" sx={{ color: 'accent.main' }} />}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {new Date(u.dateCreated).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title="Impersonate user">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleImpersonate(u)}
                                                                disabled={impersonatingId === u.userID}
                                                            >
                                                                {impersonatingId === u.userID
                                                                    ? <CircularProgress size={16} />
                                                                    : <SwitchAccountIcon fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={u.isActive ? 'Deactivate' : 'Activate'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color={u.isActive ? 'warning' : 'success'}
                                                                onClick={() => handleToggleActive(u)}
                                                                disabled={togglingId === u.userID}
                                                            >
                                                                {togglingId === u.userID
                                                                    ? <CircularProgress size={16} />
                                                                    : u.isActive ? <PersonOffIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Delete user">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDelete(u.userID)}
                                                                disabled={deletingId === u.userID}
                                                            >
                                                                {deletingId === u.userID
                                                                    ? <CircularProgress size={16} />
                                                                    : <DeleteIcon fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {usersTotalPages > 1 && (
                                <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {(usersPage - 1) * PAGE_SIZE + 1}–{Math.min(usersPage * PAGE_SIZE, usersTotal)} of {usersTotal}
                                    </Typography>
                                    <Pagination count={usersTotalPages} page={usersPage} onChange={(_, p) => setUsersPage(p)} size="small" />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            )}

            {/* Issuers Panel */}
            {activeTab === 'issuers' && (
                <Paper elevation={0} sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.3)', borderRadius: 3 }}>
                    <Box sx={{ px: 2, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={600}>
                            Issuers
                            {issuersTotal > 0 && <Chip label={issuersTotal} size="small" sx={{ ml: 1 }} />}
                        </Typography>
                        <Box component="form" display="flex" gap={1} onSubmit={handleIssuersSearch}>
                            <TextField
                                size="small"
                                placeholder="Search issuers..."
                                value={issuersSearchInput}
                                onChange={e => setIssuersSearchInput(e.target.value)}
                                sx={{ width: 220 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                }}
                            />
                            <Button type="submit">Search</Button>
                        </Box>
                    </Box>

                    {issuersLoading && (
                        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                    )}
                    {!issuersLoading && issuersError && (
                        <Box p={2}><Alert severity="error">{issuersError}</Alert></Box>
                    )}
                    {!issuersLoading && !issuersError && issuersLoaded && issuers.length === 0 && (
                        <Box textAlign="center" py={6} color="text.secondary">
                            <BusinessIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Typography mt={1}>No issuers found.</Typography>
                        </Box>
                    )}
                    {!issuersLoading && !issuersError && issuers.length > 0 && (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#2A1870', '& .MuiTableCell-head': { color: '#fff' } }}>
                                        <TableRow>
                                            <TableCell><strong>Issuer Name</strong></TableCell>
                                            <TableCell><strong>Issuer Type</strong></TableCell>
                                            <TableCell><strong>Organization Type</strong></TableCell>
                                            <TableCell><strong>Creator</strong></TableCell>
                                            <TableCell align="center" colSpan={3}>
                                                <strong>Badges</strong>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ py: 0 }} />
                                            <TableCell sx={{ py: 1 }}><strong>Created</strong></TableCell>
                                            <TableCell sx={{ py: 1 }}><strong>Active</strong></TableCell>
                                            <TableCell sx={{ py: 1 }}><strong>Inactive</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {issuers.map(issuer => (
                                            <TableRow key={issuer.issuerID} hover>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1.5}>
                                                        <Avatar src={issuer.imageUrl ?? undefined} sx={{ width: 30, height: 30 }}>
                                                            {issuer.issuerName[0] ?? '?'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {issuer.issuerName}
                                                            </Typography>
                                                            {!issuer.isActive && (
                                                                <Chip label="Inactive" size="small" sx={{ mt: 0.5 }} />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {splitCamelCase(issuer.issuerType)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color={issuer.organizationType ? 'text.primary' : 'text.secondary'}>
                                                        {issuer.organizationType ? splitCamelCase(issuer.organizationType) : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {issuer.creatorName?.trim() || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {issuer.totalBadgesCreated}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={issuer.totalActiveBadges}
                                                        size="small"
                                                        color={issuer.totalActiveBadges > 0 ? 'success' : 'default'}
                                                        variant={issuer.totalActiveBadges > 0 ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={issuer.totalInactiveBadges}
                                                        size="small"
                                                        color={issuer.totalInactiveBadges > 0 ? 'warning' : 'default'}
                                                        variant={issuer.totalInactiveBadges > 0 ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {issuersTotalPages > 1 && (
                                <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {(issuersPage - 1) * PAGE_SIZE + 1}–{Math.min(issuersPage * PAGE_SIZE, issuersTotal)} of {issuersTotal}
                                    </Typography>
                                    <Pagination count={issuersTotalPages} page={issuersPage} onChange={(_, p) => setIssuersPage(p)} size="small" />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            )}
            {/* Badges Panel */}
            {activeTab === 'badges' && (
                <Paper elevation={0} sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.3)', borderRadius: 3 }}>
                    <Box sx={{ px: 2, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={600}>
                            Badges
                            {badgesTotal > 0 && <Chip label={badgesTotal} size="small" sx={{ ml: 1 }} />}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={badgesType}
                                onChange={(_, value: 'all' | 'regular' | 'learningpath' | 'manual' | null) => {
                                    if (value === null) return;
                                    setBadgesType(value);
                                    setBadgesPage(1);
                                }}
                            >
                                <ToggleButton value="all">All Types</ToggleButton>
                                <ToggleButton value="regular">Badges</ToggleButton>
                                <ToggleButton value="learningpath">Stacked</ToggleButton>
                                <ToggleButton value="manual">Manual</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={badgesPublicity}
                                onChange={(_, value: 'all' | 'public' | 'private' | null) => {
                                    if (value === null) return;
                                    setBadgesPublicity(value);
                                    setBadgesPage(1);
                                }}
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="public">Public</ToggleButton>
                                <ToggleButton value="private">Private</ToggleButton>
                            </ToggleButtonGroup>
                            <Box component="form" display="flex" gap={1} onSubmit={handleBadgesSearch}>
                                <TextField
                                    size="small"
                                    placeholder="Search badges..."
                                    value={badgesSearchInput}
                                    onChange={e => setBadgesSearchInput(e.target.value)}
                                    sx={{ width: 220 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                    }}
                                />
                                <Button type="submit">Search</Button>
                            </Box>
                        </Box>
                    </Box>

                    {badgesLoading && (
                        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                    )}
                    {!badgesLoading && badgesError && (
                        <Box p={2}><Alert severity="error">{badgesError}</Alert></Box>
                    )}
                    {!badgesLoading && !badgesError && badgesLoaded && badges.length === 0 && (
                        <Box textAlign="center" py={6} color="text.secondary">
                            <WorkspacePremiumIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Typography mt={1}>No badges found.</Typography>
                        </Box>
                    )}
                    {!badgesLoading && !badgesError && badges.length > 0 && (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#2A1870', '& .MuiTableCell-head': { color: '#fff' } }}>
                                        <TableRow>
                                            <TableCell><strong>Name</strong></TableCell>
                                            <TableCell><strong>Issuer</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Criteria</strong></TableCell>
                                            <TableCell align="center"><strong>Issued</strong></TableCell>
                                            <TableCell align="center"><strong>Revoked</strong></TableCell>
                                            <TableCell><strong>First Issued</strong></TableCell>
                                            <TableCell><strong>Last Issued</strong></TableCell>
                                            <TableCell align="right"><strong>Actions</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {badges.map(b => {
                                            const busy = badgeActionId === b.openBadgeID;
                                            return (
                                                <TableRow key={b.openBadgeID} hover>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Avatar src={b.image ?? undefined} variant="rounded" sx={{ width: 30, height: 30 }}>
                                                                <WorkspacePremiumIcon fontSize="small" />
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={600}>{b.badgeName}</Typography>
                                                                {b.isPrivate && <Chip label="Private" size="small" sx={{ mt: 0.5 }} />}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{b.issuerName}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={b.isActive ? 'Active' : 'Inactive'} color={b.isActive ? 'success' : 'default'} size="small" />
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 220 }}>
                                                        {(() => {
                                                            const text = b.criteriaHTML ? stripHtml(b.criteriaHTML) : '';
                                                            if (!text) return <Typography variant="body2" color="text.secondary">—</Typography>;
                                                            const truncated = text.length > CRITERIA_MAX_LEN ? `${text.slice(0, CRITERIA_MAX_LEN)}…` : text;
                                                            return (
                                                                <Tooltip title={text} arrow>
                                                                    <Typography variant="body2" color="text.secondary">{truncated}</Typography>
                                                                </Tooltip>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" fontWeight={600}>{b.totalBadgesIssued}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={b.totalBadgesRevoked}
                                                            size="small"
                                                            color={b.totalBadgesRevoked > 0 ? 'warning' : 'default'}
                                                            variant={b.totalBadgesRevoked > 0 ? 'filled' : 'outlined'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {b.firstIssuedDate ? new Date(b.firstIssuedDate).toLocaleDateString() : '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {b.lastIssuedDate ? new Date(b.lastIssuedDate).toLocaleDateString() : '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                        {b.totalBadgesIssued === 0 && (
                                                            <Tooltip title="Transfer to default issuer">
                                                                <span>
                                                                    <IconButton size="small" color="warning" onClick={() => handleBadgeTransfer(b)} disabled={busy}>
                                                                        {busy ? <CircularProgress size={16} /> : <SwapHorizIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Reset badge (clear task completions)">
                                                            <span>
                                                                <IconButton size="small" color="info" onClick={() => handleBadgeReset(b)} disabled={busy}>
                                                                    {busy ? <CircularProgress size={16} /> : <RestartAltIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Delete badge">
                                                            <span>
                                                                <IconButton size="small" color="error" onClick={() => handleBadgeDelete(b)} disabled={busy}>
                                                                    {busy ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {badgesTotalPages > 1 && (
                                <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {(badgesPage - 1) * PAGE_SIZE + 1}–{Math.min(badgesPage * PAGE_SIZE, badgesTotal)} of {badgesTotal}
                                    </Typography>
                                    <Pagination count={badgesTotalPages} page={badgesPage} onChange={(_, p) => setBadgesPage(p)} size="small" />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            )}
        </Container>
    );
}
